import React, { useState, useEffect } from 'react';
import en from '../../../translations/en.json';
import { apiClient } from '../../../api/apiClient';
import { GET_ARTIST_ALBUMS_URL } from '../../../api/artist';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';

function AlbumsTab({ artist, onSelectAlbum }) {
  const [albums, setAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    let isMounted = true;
    if (artist && artist.id) {
      setIsLoading(true);
      const fetchArtistAlbums = async () => {
        try {
          const res = await apiClient(GET_ARTIST_ALBUMS_URL(artist.id));
          if (res && res.albums && isMounted) {
            setAlbums(res.albums);
          }
        } catch (err) {
          console.error('Failed to fetch artist albums:', err);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      };
      fetchArtistAlbums();
    }
    return () => {
      isMounted = false;
    };
  }, [artist?.id]);

  // Extract unique Record Labels from returned albums
  const uniqueLabelsMap = {};
  albums.forEach(alb => {
    if (alb.record_label && alb.record_label.id) {
      uniqueLabelsMap[alb.record_label.id] = alb.record_label;
    } else if (alb.recordLabelName && alb.recordLabelName !== '—') {
      const fallbackKey = alb.recordLabelName;
      uniqueLabelsMap[fallbackKey] = {
        id: fallbackKey,
        name: alb.recordLabelName,
        display_name: alb.recordLabelName,
        image_url: alb.recordLabelImage || null
      };
    }
  });
  const recordLabels = Object.values(uniqueLabelsMap);

  const colorClasses = ['label-purple', 'label-blue', 'label-green', 'label-orange'];

  return (
    <div className="artist-albums-tab">
      {/* 1. Record Labels Section */}
      <div className="labels-section">
        <h4 className="section-title">
          {t('artistDetails.albumsRecordLabels')} ({recordLabels.length})
        </h4>
        {isLoading ? (
          <Loader label="Fetching record labels, please wait." />
        ) : recordLabels.length === 0 ? (
          <EmptyState title="No Record Labels" subtitle="This artist has no associated record labels yet." />
        ) : (
          <div className="labels-grid">
            {recordLabels.map((lbl, idx) => {
              const colorClass = colorClasses[idx % colorClasses.length];
              const img = lbl.image_url || lbl.imageUrl;
              const labelName = lbl.display_name || lbl.name || 'Record Label';
              const initial = labelName.charAt(0).toUpperCase();

              return (
                <div key={lbl.id || idx} className="label-badge-card">
                  <div className="label-card-thumb">
                    {img ? (
                      <img 
                        src={img} 
                        alt={labelName} 
                        className="label-card-img-thumb"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement.querySelector('.label-icon-wrapper');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`label-icon-wrapper ${colorClass}`}
                      style={{ display: img ? 'none' : 'flex' }}
                    >
                      <span className="label-initial">{initial}</span>
                    </div>
                  </div>
                  <div className="label-card-info">
                    <span className="label-card-name" title={labelName}>{labelName}</span>
                    <span className="label-card-subtext">Record Label</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. Albums Section */}
      <div className="albums-section">
        <div className="section-header-row">
          <h4 className="section-title">
            {t('artistDetails.albumsTitle')} ({albums.length})
          </h4>
          {/* Note: Delete button removed per requirement */}
        </div>

        {isLoading ? (
          <Loader label="Fetching albums, please wait." />
        ) : albums.length === 0 ? (
          <EmptyState title="No Albums Found" subtitle="This artist has no albums associated yet." />
        ) : (
          <div className="albums-grid">
            {albums.map((alb) => (
              <div 
                key={alb.id} 
                className="album-card-item"
                onClick={() => onSelectAlbum && onSelectAlbum(alb)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-top-header">
                  <button type="button" className="album-dots-btn" aria-label="Album options">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                </div>
                <div className="album-img-box">
                  <img 
                    src={alb.coverUrl || alb.imageUrl || alb.image_url || 'https://images.unsplash.com/photo-1506157786151-b8491531f063?auto=format&fit=crop&q=80&w=200&h=200'} 
                    alt={alb.name} 
                    className="album-cover" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1506157786151-b8491531f063?auto=format&fit=crop&q=80&w=200&h=200';
                    }}
                  />
                </div>
                <div className="album-info-body">
                  <h5 className="album-name-title">{alb.name}</h5>
                  <div className="album-metadata-text">
                    <span>📅 {alb.release_year || alb.releaseYear || '—'} • {alb.track_count || alb.songsCount || 0} Songs</span>
                  </div>
                </div>
                <div className="album-card-action">
                  <button 
                    type="button" 
                    className="view-album-link" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectAlbum && onSelectAlbum(alb);
                    }}
                  >
                    {t('artistDetails.albumsViewLink')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AlbumsTab;
